import {
  useState,
  useEffect,
  useCallback,
} from 'react';
import { Linking } from 'react-native';

import {
  useLinkTo,
  useUserStateRedux,
  useIsEnabled,
} from '../../../../hooks';
import analytics from '../../../../modules/analytics';
import { PRO_CLIENT_VIEW } from '../../../../route-names';

const DISABLE_AUTOMATED_NCD_FEE_REFUND_SWITCH = 'automate_ncd_fee_refund_KILLSWITCH_fly_3507_20221109';

export enum NotificationTypes {
  Client = 'client_appt_cancel',
  Provider = 'appointment_cancelled_provider',
  NoShow = 'appointment_cancelled_provider_no_show',
}

export enum NotificationComponents {
  Client = 'client_appt_cancel',
  NcdClient = 'ncd_client_appt_cancel',
  NcdFeeRefundedClient = 'ncd_fee_refunded_client_appt_cancel',
  Provider = 'appointment_cancelled_provider',
  NcdProvider = 'ncd_appointment_cancelled_provider',
  NoShow = 'appointment_cancelled_provider_no_show',
  NcdNoShow = 'ncd_appointment_cancelled_provider_no_show',
}

export type NotificationProps = {
  object_type: 'Appointment';
  object_id: string | number;
  provider_id: string | number;
  client_id: string | number;
  service_name: string;
  client_name: string;
  creation_time: string;
  local_start: string;
  paid_ncd_fee: boolean;
  type: NotificationTypes;
};

type AppointmentCancelledNotificationData = {
  notificationComponent: string | null;
  handleCTAClick: () => void;
  handleNotificationClick: () => void;
  startTime: string;
  clientName: string;
  serviceName: string;
};

const useAppointmentCancelledNotification = (
  notification: NotificationProps,
): AppointmentCancelledNotificationData => {
  const {
    provider_id,
    client_id,
    service_name,
    client_name,
    local_start,
    paid_ncd_fee,
    object_id: appointment_id,
    type: notification_type,
  } = notification;
  const [
    shouldHideNotification,
    setShouldHideNotification,
    shouldHideNotificationLoaded,
  ] = useUserStateRedux(`appointment-cancelled-notification-${appointment_id}`);
  const [notificationComponent, setNotificationComponent] = useState<string | null>(null);
  const [notificationViewed, setNotificationViewed] = useState<boolean>(false);
  const automateNcdFeeRefundDisabled = useIsEnabled(DISABLE_AUTOMATED_NCD_FEE_REFUND_SWITCH);
  const linkTo = useLinkTo();

  const getFormattedData = () => {
    const localStart = new Date(local_start);
    const dateString = localStart.toLocaleDateString(
      'en-US',
      {
        weekday: 'long',
        year: undefined,
        month: 'long',
        day: 'numeric',
      },
    );

    const timeString = localStart.toLocaleTimeString(
      'en-US',
      {
        hour12: true,
        hour: '2-digit',
        minute: '2-digit',
      },
    );
    const startTime = `${dateString} at ${timeString}`;
    const clientName = client_name;
    const serviceName = service_name;
    return {
      startTime,
      clientName,
      serviceName,
    };
  };

  const getEventName = useCallback(() => {
    const ncd = paid_ncd_fee ? 'NCD' : '';
    if (notification_type === NotificationTypes.Client) return `Client Cancelled ${ncd} Appt Notification`;
    if (notification_type === NotificationTypes.Provider) return `Provider Cancelled ${ncd} Appt Notification`;
    if (notification_type === NotificationTypes.NoShow) return `Provider Cancelled (No Show) ${ncd} Appt Notification`;
    return null;
  }, [
    paid_ncd_fee,
    notification_type,
  ]);

  const handleCTAClick = useCallback(() => {
    const subjectLine = encodeURIComponent(`NCD charge: Refund request for appointment ID ${appointment_id}`);
    const disputeLink = `mailto:cx@styleseat.com?subject=${subjectLine}`;
    analytics.track(`${getEventName()} CTA Clicked`, {
      provider_id,
      appointment_id,
    });
    setShouldHideNotification(true);
    setNotificationComponent(null);
    Linking.openURL(disputeLink);
  }, [
    provider_id,
    appointment_id,
    getEventName,
    setShouldHideNotification,
  ]);

  const handleNotificationClick = useCallback(() => {
    analytics.track(`${getEventName()} Clicked`, {
      provider_id,
      appointment_id,
    });
    setShouldHideNotification(true);
    setNotificationComponent(null);
    linkTo(PRO_CLIENT_VIEW, { clientId: client_id });
  }, [
    provider_id,
    appointment_id,
    client_id,
    getEventName,
    setShouldHideNotification,
    linkTo,
  ]);

  useEffect(() => {
    if (
      notificationViewed
      || !paid_ncd_fee
      || !shouldHideNotificationLoaded
      || shouldHideNotification
    ) return;
    analytics.track(`${getEventName()} Viewed`, {
      provider_id,
      appointment_id,
    });
    setNotificationViewed(true);
  }, [
    notificationViewed,
    paid_ncd_fee,
    shouldHideNotificationLoaded,
    shouldHideNotification,
    appointment_id,
    provider_id,
    getEventName,
  ]);

  useEffect(() => {
    if (!paid_ncd_fee) setNotificationComponent(notification_type);
    else if (
      shouldHideNotificationLoaded
      && !shouldHideNotification
    ) {
      if (!automateNcdFeeRefundDisabled && notification_type === NotificationTypes.Client) setNotificationComponent(`ncd_fee_refunded_${notification_type}`);
      else setNotificationComponent(`ncd_${notification_type}`);
    }
  }, [
    shouldHideNotificationLoaded,
    shouldHideNotification,
    paid_ncd_fee,
    notification_type,
    automateNcdFeeRefundDisabled,
  ]);

  return {
    notificationComponent,
    handleCTAClick,
    handleNotificationClick,
    ...getFormattedData(),
  };
};

export default useAppointmentCancelledNotification;
